import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '@env/environment';

import { BehaviorSubject, distinctUntilChanged, filter, tap } from 'rxjs';

import { AuthConfig, OAuthErrorEvent, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { LoggerService } from './logger.service';

export type LoginProvider = 'okta' | 'alibaba';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  //#region Const

  private readonly STORAGE_KEY = 'loginProvider';

  private readonly AUTH_CONFIG = {
    redirectUri: `${environment.services.baseUri}/app/login/callback`,
    postLogoutRedirectUri: `${environment.services.baseUri}/app`,

    // Prevent duplicate refresh timers when ID token and access token have different expiration times
    // See: https://github.com/manfredsteyer/angular-oauth2-oidc/issues/1331
    disableIdTokenTimer: true,
    responseType: 'code',
    scope: 'openid profile email offline_access',
  };

  //#endregion

  //#region Declarations

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  currentProvider: LoginProvider | null = null;
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable().pipe(
    distinctUntilChanged(), // Prevent duplicate emissions of the same value
  );

  //#endregion

  //#region Properties

  get isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken();
  }

  //#endregion

  constructor(
    private router: Router,
    private logger: LoggerService,
    private oauthService: OAuthService,
  ) {
    this.setupEventHandlers();
    this.initializeProvider();
  }

  //#region Methods

  /**
   * Process callback after login
   * @returns Promise<boolean> - true if login was successful
   */
  async handleCallback(): Promise<boolean> {
    try {
      await this.oauthService.tryLoginCodeFlow();

      // If we reach here, the code flow succeeded
      // Check if we actually have valid tokens
      const hasValidTokens = this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken();

      if (hasValidTokens) {
        this.logger.log('✅ handleCallback succeeded with valid tokens');
        this.logInfo();
        this.updateAuthenticationStatus();
        return true;
      } else {
        this.logger.log('❌ handleCallback failed - no valid tokens received');
        this.updateAuthenticationStatus();
        return false;
      }
    } catch (error) {
      this.logger.error('❌ Callback error:', error);
      this.updateAuthenticationStatus();
      return false;
    }
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  /**
   * Get ID token
   */
  getIdToken(): string | null {
    return this.oauthService.getIdToken();
  }

  /**
   * Start login process
   */
  initLoginFlow(): void {
    this.oauthService.initLoginFlow();
  }

  /**
   * Initialize login flow with specific provider
   * @param provider - Login provider to use (okta or alibaba)
   */
  loginWithProvider(provider: LoginProvider): void {
    this.setProvider(provider);
    this.oauthService.initLoginFlow();
  }

  /**
   * Start logout process
   */
  logOut(): void {
    this.oauthService.logOut();
    this.isAuthenticatedSubject.next(false);
    this.clearProvider();
  }

  //#endregion

  //#region Helpers

  /**
   * Configure OAuth service
   */
  private configureProvider(): void {
    this.logger.log(`🔧 Configuring OAuth for provider: ${this.currentProvider}`);

    // Configure OAuth service based on selected provider
    const authConfig = this.getConfiguration();
    this.logger.log('🔧 Auth configuration:', authConfig);
    this.oauthService.configure(authConfig);

    this.oauthService.tryLogin().then(() => {
      this.logger.log('✅ Try login completed, with isAuthenticated as', this.isAuthenticated);
      this.updateAuthenticationStatus();

      // Setup automatic silent refresh
      this.oauthService.setupAutomaticSilentRefresh();
    });
  }

  /**
   * Returns the authentication configuration based on the current provider.
   *
   * Depending on the value of `this.currentProvider`, this method constructs and returns
   * an `AuthConfig` object with provider-specific endpoints and credentials.
   * Supported providers include 'okta' and 'alibaba'.
   *
   * @returns {AuthConfig} The authentication configuration for the current provider.
   */
  private getConfiguration(): AuthConfig {
    switch (this.currentProvider) {
      case 'okta':
        return {
          issuer: `${environment.oktaDomain}/oauth2/default`,
          clientId: environment.oktaClientId,

          tokenEndpoint: `${environment.oktaDomain}/oauth2/default/v1/token`,
          userinfoEndpoint: `${environment.oktaDomain}/oauth2/default/v1/userinfo`,
          loginUrl: `${environment.oktaDomain}/oauth2/default/v1/authorize`,
          logoutUrl: `${environment.oktaDomain}/oauth2/default/v1/logout`,

          ...this.AUTH_CONFIG,
        };
      case 'alibaba':
        return {
          issuer: `${environment.cloudSsoBaseUrl}/${environment.cloudSsoInstanceId}/${environment.cloudSsoApplicationId}/oidc`,
          clientId: environment.cloudSsoApplicationId,
          tokenEndpoint: `${environment.cloudSsoProxy}/oauth2/token`,
          userinfoEndpoint: `${environment.cloudSsoProxy}/oauth2/userinfo`,
          loginUrl: `${environment.cloudSsoFrontend}/oauth2/authorize`,
          logoutUrl: `${environment.cloudSsoFrontend}/oauth2/logout`,

          ...this.AUTH_CONFIG,
        };
    }
  }

  /**
   * Setup OAuth event handlers
   */
  private setupEventHandlers(): void {
    // Filter only OAuthSuccessEvent and OAuthErrorEvent types
    this.oauthService.events
      .pipe(
        tap((event) => this.logger.log('🔔 OAuth event:', event)),
        filter((e) => e.type === 'token_received' || e.type === 'token_refreshed' || e.type === 'token_error'),
      )
      .subscribe((event: OAuthEvent) => {
        if (event.type === 'token_received') {
          this.logger.log('✅ Token received successfully');
          this.logInfo();
          this.updateAuthenticationStatus();
        } else if (event.type === 'token_refreshed') {
          this.logger.log('✅ Token refreshed successfully');
          this.logInfo();
          this.updateAuthenticationStatus();
        } else if (event.type === 'token_error') {
          this.logger.error('❌ Token error:', event);
          this.updateAuthenticationStatus();
        }
      });

    // Handle specific errors
    this.oauthService.events.pipe(filter((e) => e instanceof OAuthErrorEvent)).subscribe((event: OAuthErrorEvent) => {
      this.logger.error('❌ OAuth error:', event);
    });
  }

  /**
   * Update authentication status
   */
  private updateAuthenticationStatus(): void {
    this.isAuthenticatedSubject.next(this.isAuthenticated);
  }

  /**
   * Logs authentication-related information to the console for debugging purposes.
   * Specifically, it outputs a truncated version of the access token and ID token.
   * Uncomment the identity claims line to log user identity claims as well.
   */
  private logInfo(): void {
    this.logger.log('🔑 Access Token:', this.oauthService.getAccessToken()?.substring(0, 20) + '...');
    this.logger.log('🆔 ID Token:', this.oauthService.getIdToken()?.substring(0, 20) + '...');
  }

  //#region Provider Management

  /**
   * Initialize provider from storage on service construction
   */
  private initializeProvider(): void {
    const storedProvider = this.getProviderFromStorage();
    if (storedProvider) {
      this.setProvider(storedProvider);
    }
  }

  /**
   * Set and configure the authentication provider
   * @param provider Login provider to configure
   */
  private setProvider(provider: LoginProvider): void {
    this.currentProvider = provider;
    this.saveProviderToStorage();
    this.configureProvider();
  }

  /**
   * Save provider to local storage
   * @param provider Login provider to save
   */
  private saveProviderToStorage(): void {
    localStorage.setItem(this.STORAGE_KEY, this.currentProvider);
  }

  /**
   * Get provider from local storage
   * @returns LoginProvider if found and valid, null otherwise
   */
  private getProviderFromStorage(): LoginProvider | null {
    const storedProvider = localStorage.getItem(this.STORAGE_KEY) as LoginProvider;
    if (storedProvider && (storedProvider === 'okta' || storedProvider === 'alibaba')) {
      return storedProvider;
    }
    return null;
  }

  /**
   * Clear provider from local storage and reset current provider
   */
  private clearProvider(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.currentProvider = null;
  }

  //#endregion

  //#endregion
}
